import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

interface QuickTooltipState {
  text: string;
  placement: 'above' | 'below';
  style: h.JSX.CSSProperties;
}

const QUICK_TOOLTIP_SHOW_DELAY_MS = 150;
const QUICK_TOOLTIP_VISIBLE_MS = 1500;

type TooltipAnchor = EventTarget | Event | null;

interface TooltipSnapshot {
  target: HTMLElement;
  point: { x: number; y: number } | null;
}

function getAnchorElement(anchor: TooltipAnchor): HTMLElement | null {
  if (!anchor) return null;
  if ((anchor as HTMLElement).getBoundingClientRect) return anchor as HTMLElement;
  const currentTarget = (anchor as Event).currentTarget;
  if (currentTarget && (currentTarget as HTMLElement).getBoundingClientRect) return currentTarget as HTMLElement;
  const target = (anchor as Event).target;
  if (target && (target as HTMLElement).getBoundingClientRect) return target as HTMLElement;
  return null;
}

function getAnchorPoint(anchor: TooltipAnchor): { x: number; y: number } | null {
  if (!anchor || typeof (anchor as MouseEvent).clientX !== 'number') return null;
  const { clientX, clientY } = anchor as MouseEvent;
  if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
  return { x: clientX, y: clientY };
}

function getAnchorSnapshot(anchor: TooltipAnchor): TooltipSnapshot | null {
  const target = getAnchorElement(anchor);
  if (!target) return null;
  return { target, point: getAnchorPoint(anchor) };
}

function createsFixedContainingBlock(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  const backdropFilter = (style as CSSStyleDeclaration & { backdropFilter?: string }).backdropFilter;
  const willChange = style.willChange || '';
  return (
    style.transform !== 'none' ||
    style.perspective !== 'none' ||
    style.filter !== 'none' ||
    (!!backdropFilter && backdropFilter !== 'none') ||
    /\b(?:paint|layout|strict|content)\b/.test(style.contain || '') ||
    /\b(?:transform|perspective|filter|backdrop-filter)\b/.test(willChange)
  );
}

function getParentAcrossShadow(node: Node): Node | null {
  if (node instanceof ShadowRoot) return node.host;
  return node.parentNode;
}

function getFixedContainingBlockRect(target: HTMLElement): Pick<DOMRect, 'left' | 'top'> {
  let node: Node | null = getParentAcrossShadow(target);
  while (node) {
    if (node instanceof HTMLElement) {
      if (node !== document.body && node !== document.documentElement && createsFixedContainingBlock(node)) {
        return node.getBoundingClientRect();
      }
    }
    node = getParentAcrossShadow(node);
  }
  return { left: 0, top: 0 };
}

function getTooltipPosition(target: HTMLElement, point?: { x: number; y: number } | null): Pick<QuickTooltipState, 'placement' | 'style'> {
  const rect = target.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const containingBlockRect = getFixedContainingBlockRect(target);
  const maxWidth = Math.min(280, Math.max(180, viewportWidth - 24));
  const baseLeft = point ? point.x + 12 : rect.left + 12;
  const baseTop = point ? point.y : rect.top;
  const viewportLeft = Math.max(12, Math.min(baseLeft, viewportWidth - maxWidth - 12));
  const hasRoomAbove = baseTop > 92;
  const rawViewportTop = point
    ? (hasRoomAbove ? point.y - 8 : point.y + 12)
    : (hasRoomAbove ? rect.top - 8 : rect.bottom + 8);
  const viewportTop = Math.max(12, Math.min(rawViewportTop, viewportHeight - 12));

  return {
    placement: hasRoomAbove ? 'above' : 'below',
    style: {
      top: `${viewportTop - containingBlockRect.top}px`,
      left: `${viewportLeft - containingBlockRect.left}px`,
      maxWidth: `${maxWidth}px`,
    },
  };
}

export function useQuickTooltip(text: string | undefined) {
  const [tooltip, setTooltip] = useState<QuickTooltipState | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const latestAnchorRef = useRef<TooltipSnapshot | null>(null);
  const visibleRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const showFromSnapshot = useCallback((snapshot: TooltipSnapshot | null) => {
    if (!text || !snapshot) {
      visibleRef.current = false;
      setTooltip(null);
      return;
    }
    const { placement, style } = getTooltipPosition(snapshot.target, snapshot.point);
    visibleRef.current = true;
    setTooltip({ text, placement, style });
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      visibleRef.current = false;
      setTooltip(null);
    }, QUICK_TOOLTIP_VISIBLE_MS);
  }, [text]);

  const showTooltip = useCallback((anchor: TooltipAnchor) => {
    clearTimers();
    latestAnchorRef.current = getAnchorSnapshot(anchor);
    if (!text || !latestAnchorRef.current) {
      visibleRef.current = false;
      setTooltip(null);
      return;
    }
    showTimerRef.current = window.setTimeout(() => {
      showTimerRef.current = null;
      showFromSnapshot(latestAnchorRef.current);
    }, QUICK_TOOLTIP_SHOW_DELAY_MS);
  }, [clearTimers, showFromSnapshot, text]);

  const moveTooltip = useCallback((anchor: TooltipAnchor) => {
    const snapshot = getAnchorSnapshot(anchor);
    if (snapshot) latestAnchorRef.current = snapshot;
    if (!visibleRef.current) return;
    if (!text || !latestAnchorRef.current) return;
    const { placement, style } = getTooltipPosition(latestAnchorRef.current.target, latestAnchorRef.current.point);
    setTooltip({ text, placement, style });
  }, [text]);

  const hideTooltip = useCallback(() => {
    clearTimers();
    latestAnchorRef.current = null;
    visibleRef.current = false;
    setTooltip(null);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);
  useEffect(() => {
    clearTimers();
    latestAnchorRef.current = null;
    visibleRef.current = false;
    setTooltip(null);
  }, [clearTimers, text]);

  return { tooltip, showTooltip, moveTooltip, hideTooltip };
}

export function QuickTooltip({ tooltip }: { tooltip: QuickTooltipState | null }) {
  if (!tooltip) return null;

  return (
    <span
      class={`ts-quick-tooltip ts-quick-tooltip--${tooltip.placement}`}
      role="tooltip"
      style={tooltip.style}
    >
      {tooltip.text}
    </span>
  );
}
